import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Tooltip from "@mui/material/Tooltip";
import { ThemedTrashIcon } from "assets/CXFIcons/Triggers";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { ACTION_TYPES } from "constants/flowDesigner";
import { tryCatchWrapper } from "helpers/common";
import useAllowEditWorkflow from "hooks/useAllowEditWorkflow";
import { useDebouncer } from "hooks/useDebouncer";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import { ComponentPropsContext, ComponentPropsContextApi } from "layouts/Flow Designer/context";
import { isVariableValueEmpty } from "layouts/Flow Designer/helpers/common";
import { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { useReactFlow } from "reactflow";
import { styles } from "../..";
import AutocompleteInput from "../AutocompleteInput";
import { StyledSelect, TextField } from "../StyledComponents";

const DATA_TYPE_OPTIONS = [
   { label: "array", value: "array" },
   { label: "boolean", value: "boolean" },
   { label: "number", value: "number" },
   { label: "object", value: "object" },
   { label: "string", value: "string" }
];
const BOOLEAN_LITERAL_OPTIONS = ["true", "false"];

const getDefaultValueForDataType = (dataType) => {
   switch (dataType) {
      case "object":
         return "{}";
      case "array":
         return "[]";
      default:
         return "";
   }
};

const getStartupDefaultValue = (variable) => variable?.defaultInput?.value ?? "";

const isContextReference = (value) => typeof value === "string" && value.startsWith("@");

const isAllowedNumberInput = (value) => typeof value !== "string" || value === "" || isContextReference(value) || /^-?(?:\d+\.?\d*|\.\d*)?(?:[eE][+-]?\d*)?$/.test(value);

const getNodeDisplayName = (node) => {
   const name = node.data?.customLabel || node.data?.label || node.data?.flowElement?.action || node.type || "Unnamed node";
   const key = node.data?.componentKey || node.id;

   return `${name} (${key})`;
};

export const StringInput = ({ action, getFieldValue }) => {
   const { actionForm, formErrors, componentAction } = useContext(ComponentPropsContext);
   const { setActionForm, setComponentAction, setFormErrors } = useContext(ComponentPropsContextApi);
   const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const { selectedNode } = useSelector((store) => store?.designer?.componentPropsPanel);
   const { getNodes } = useReactFlow();
   const isFlowActive = currentSavedFlow?.shared_status === "active";
   const { allowEditWorkflow } = useAllowEditWorkflow();
   const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(currentSavedFlow?.id);
   const [firstValue, setFirstValue] = useState(getFieldValue(action));
   const [secondValue, setSecondValue] = useState(actionForm[action.field]?.secondaryInput?.value ?? "");
   const [defaultValue, setDefaultValue] = useState(getStartupDefaultValue(actionForm[action.field]));
   const [blockedDeletion, setBlockedDeletion] = useState(null);
   const isTypedValueField = action.field?.startsWith("var_") && ["Variables", "Invoke"].includes(componentAction?.actionName);
   const dataType = actionForm[action.field]?.dataType || action.dataType;
   const isDisabled = isFlowActive || !allowEditWorkflow(isReadOnlyAccess);
   const { debouncer: firstDebouncer } = useDebouncer(handleFormInputChange);
   const { debouncer: secondDebouncer } = useDebouncer(handleFormSecondaryInputChange);
   const { debouncer: defaultDebouncer } = useDebouncer(handleFormDefaultInputChange);

   const handleSetFirstValue = (event, val) => {
      setFirstValue(val ? val : event.target?.value);
      firstDebouncer(event);
   };

   function handleFormInputChange() {
      const key = action.field;
      const actionFormCopy = { ...actionForm };
      setFormErrors((prev) => prev.filter((error) => error !== key && error !== `invalid_variable_name_${key}`));
      if (actionFormCopy[key]?.isPassword) {
         actionFormCopy[key] = { ...actionFormCopy[key], value: firstValue, isPassword: true, isEncoded: false };
      }
      const updatedField = { ...actionFormCopy[key], value: firstValue, isVariable: false };
      if (isTypedValueField) updatedField.dataType = dataType;
      setActionForm({ ...actionFormCopy, [key]: updatedField });
   }

   const handleSetSecondValue = (event, val) => {
      setSecondValue(val ? val : event.target?.value);
      secondDebouncer(event);
   };

   function handleFormSecondaryInputChange() {
      setActionForm((current) => ({
         ...current,
         [action.field]: {
            ...current[action.field],
            secondaryInput: { ...current[action.field]?.secondaryInput, value: secondValue }
         }
      }));
   }

   const updateTypedValue = (value) => {
      setSecondValue(value);
      setFormErrors((prev) => prev.filter((error) => error !== `invalid_variable_value_${action.field}`));
      secondDebouncer(value);
   };

   const handleSetTypedValue = (event, val) => {
      const inputValue = val ?? event.target?.value;
      if (dataType === "number" && !isAllowedNumberInput(inputValue)) return;

      updateTypedValue(inputValue);
   };

   const updateTypedDefaultValue = (value, debounced = false, debounceValue) => {
      setDefaultValue(value);
      setFormErrors((prev) => prev.filter((error) => error !== `invalid_variable_default_${action.field}`));
      if (debounced) {
         defaultDebouncer(debounceValue);
         return;
      }
      setActionForm((current) => ({
         ...current,
         [action.field]: {
            ...current[action.field],
            dataType,
            defaultInput: {
               ...action.defaultInput,
               ...current[action.field]?.defaultInput,
               field: current[action.field]?.defaultInput?.field ?? `defVal_${action.field.split("_").pop()}`,
               label: "Default value",
               isRequired: false,
               value
            }
         }
      }));
   };

   function handleFormDefaultInputChange() {
      setActionForm((current) => ({
         ...current,
         [action.field]: {
            ...current[action.field],
            defaultInput: {
               ...current[action.field]?.defaultInput,
               field: current[action.field]?.defaultInput?.field ?? `defVal_${action.field.split("_").pop()}`,
               label: "Default value",
               isRequired: false,
               value: defaultValue
            }
         }
      }));
   }

   const handleSetTypedDefaultValue = (event, val) => {
      const inputValue = val ?? event.target?.value;

      if (dataType === "number" && !isAllowedNumberInput(inputValue)) return;

      const value = dataType === "number" && val === undefined && event.target?.type === "number" && event.target.value !== "" ? event.target.valueAsNumber : inputValue;
      updateTypedDefaultValue(value, true, event);
   };

   const handleSetDefaultValue = (event, val) => {
      setDefaultValue(val ? val : event.target?.value);
      defaultDebouncer(event);
   };

   const handleDataTypeChange = (nextDataType) => {
      const value = getDefaultValueForDataType(nextDataType);
      const nextDefaultValue = value;
      setSecondValue(value);
      setDefaultValue(nextDefaultValue);
      setFormErrors((prev) => prev.filter((error) => error !== `invalid_variable_value_${action.field}` && error !== `invalid_variable_default_${action.field}`));
      setActionForm((current) => {
         const variable = current[action.field] ?? {};

         return {
            ...current,
            [action.field]: {
               ...variable,
               dataType: nextDataType,
               secondaryInput: { ...action.secondaryInput, ...variable.secondaryInput, value },
               defaultInput: {
                  ...action.defaultInput,
                  ...variable.defaultInput,
                  field: variable.defaultInput?.field ?? `defVal_${action.field.split("_").pop()}`,
                  label: "Default value",
                  isRequired: false,
                  value: nextDefaultValue
               }
            }
         };
      });
   };

   const handleTextFieldBlur = (targetAction, isSecondary) => {
      const textValue = isSecondary ? actionForm[targetAction.field]?.secondaryInput?.value : firstValue;
      if (!textValue && targetAction.isRequired) setFormErrors((prev) => (prev.includes(targetAction.field) ? prev : [...prev, targetAction.field]));
      else setFormErrors((prev) => prev?.filter((error) => error !== targetAction.field));
   };

   const findOutputReferenceConsumers = (field) => {
      const referenceName = actionForm[field]?.value ?? firstValue;
      const variableNode = getNodes().find((node) => node.id === selectedNode?.elementId);
      const componentKey = variableNode?.data?.componentKey ?? selectedNode?.data?.componentKey;

      if (!referenceName || !componentKey) return [];

      const referencePath = `variables.${componentKey}.${referenceName}`;
      return getNodes().filter((node) => node.data?.variable_reference === referencePath);
   };

   const deleteVariable = (field) => {
      if (isFlowActive || !allowEditWorkflow(isReadOnlyAccess)) return;

      if (field.startsWith("reference_var_")) {
         const consumers = findOutputReferenceConsumers(field);
         if (consumers.length) {
            setBlockedDeletion({
               name: actionForm[field]?.value ?? firstValue,
               consumers
            });
            return;
         }
      }

      setActionForm((current) => {
         const copy = { ...current };
         delete copy[field];
         return copy;
      });
      setComponentAction((current) => {
         const copy = { ...current };
         if (componentAction.actionName === "Invoke") {
            copy.dataInsideAction = copy.dataInsideAction?.map((data) => (data.field === "parameters" ? { ...data, listItems: data.listItems?.filter((item) => item.field !== field) || [] } : data));
         } else copy.dataInsideAction = copy.dataInsideAction?.filter((data) => data.field !== field);
         return copy;
      });
   };

   const renderTypedInput = ({ label, value, isDefault = false }) => {
      const errorKey = `${isDefault ? "invalid_variable_default" : "invalid_variable_value"}_${action.field}`;
      const hasInvalidValue = formErrors.includes(errorKey);
      const helperText = hasInvalidValue
         ? dataType === "boolean"
            ? `${label} must be true, false, or an @ reference.`
            : `${label} must be a valid ${dataType === "object" ? "JSON object" : dataType === "array" ? "array" : dataType}, or an @ reference.`
         : "";
      const onChange = isDefault
         ? (dataType === "string" ? handleSetDefaultValue : handleSetTypedDefaultValue)
         : handleSetTypedValue;
      const handleBlur = () => {
         const isEmptyValue = isVariableValueEmpty(value);
         const isInvalidNumber = dataType === "number" && !isContextReference(value) && !Number.isFinite(Number(value));
         const isInvalidBoolean = dataType === "boolean" && !isEmptyValue && !isContextReference(value) && ![true, false, "true", "false"].includes(value);

         if (!isEmptyValue && (isInvalidNumber || isInvalidBoolean)) {
            setFormErrors((prev) => prev.includes(errorKey) ? prev : [...prev, errorKey]);
         } else {
            setFormErrors((prev) => prev.filter((error) => error !== errorKey));
         }
      };

      if (["object", "array"].includes(dataType)) {
         return (
            <SoftBox sx={{ minWidth: 0, width: "100%", display: "flex", flexDirection: "column" }}>
               <SoftTypography component="span" sx={{ mb: "6px", fontSize: "13px", fontWeight: 600, color: "text.primary" }}>
                  {label}
               </SoftTypography>
               <Box sx={{ mt: 0 }}>
                  <AutocompleteInput
                     action={{ ...(isDefault ? action.defaultInput : action.secondaryInput), field: errorKey, label, isRequired: false, parentField: action.field }}
                     fieldValue={value}
                     onChange={onChange}
                     onBlur={handleBlur}
                     errorHelperText={helperText}
                     hasSecondaryInput
                     secondaryInputKey={isDefault ? "defaultInput" : undefined}
                     hideLabel
                     disabled={isDisabled}
                  />
               </Box>
            </SoftBox>
         );
      }

      if (["string", "number", "boolean"].includes(dataType)) {
         return (
            <SoftBox sx={{ minWidth: 0, width: "100%", display: "flex", flexDirection: "column" }}>
               <SoftTypography component="span" sx={{ mb: "6px", fontSize: "13px", fontWeight: 600, color: "text.primary" }}>
                  {label}
               </SoftTypography>
               <Box sx={{ mt: 0 }}>
                  <AutocompleteInput
                     action={{ ...(isDefault ? action.defaultInput : action.secondaryInput), field: errorKey, label, isRequired: false, parentField: action.field }}
                     fieldValue={value}
                     onChange={onChange}
                     hasSecondaryInput
                     secondaryInputKey={isDefault ? "defaultInput" : undefined}
                     hideLabel
                     disabled={isDisabled}
                     onBlur={handleBlur}
                     errorHelperText={helperText}
                     literalOptions={dataType === "boolean" ? BOOLEAN_LITERAL_OPTIONS : undefined}
                  />
               </Box>
            </SoftBox>
         );
      }
      return (
         <TextField label={label} value={value} onChange={onChange} onBlur={handleBlur} type={dataType === "number" ? "number" : "text"} error={hasInvalidValue} helperText={helperText} disabled={isDisabled} fullWidth required={!isDefault} />
      );
   };

   if (isTypedValueField) {
      return (
         <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto", alignItems: "start", width: "100%", gap: 1, mt: 1 }}>
            <TextField
               label="Name"
               helperText={formErrors.includes(`duplicate_variable_name_${action.field}`) ? "Names must be unique." : formErrors.includes(action.field) ? "Name is required." : ""}
               error={formErrors.includes(action.field) || formErrors.includes(`duplicate_variable_name_${action.field}`)}
               name={action.field}
               onChange={handleSetFirstValue}
               onBlur={() => tryCatchWrapper(handleTextFieldBlur)(action)}
               value={firstValue}
               type="text"
               variant="outlined"
               required
               InputLabelProps={{ classes: { asterisk: styles.asterisk } }}
               disabled={isDisabled}
               fullWidth
            />
            <StyledSelect
               placeholder="Select type"
               label="Type"
               isRequired
               isSearchable={false}
               value={DATA_TYPE_OPTIONS.find((option) => option.value === dataType)}
               options={DATA_TYPE_OPTIONS}
               onChange={(option) => handleDataTypeChange(option.value)}
               isDisabled={isDisabled}
               sx={{ mt: "25px" }}
            />
            {renderTypedInput({ label: "Value", value: secondValue })}
            {renderTypedInput({ label: "Default value", value: defaultValue, isDefault: true })}
            {!isDisabled && (
               <Tooltip placement="left" title="Delete">
                  <IconButton
                     className="delete-variable-icon"
                     sx={{ mt: "25px" }}
                     onClick={(event) => {
                        event.stopPropagation();
                        deleteVariable(action.field);
                     }}
                  >
                     <ThemedTrashIcon />
                  </IconButton>
               </Tooltip>
            )}
         </Box>
      );
   }

   return (
      <>
         {action.secondaryInput ? (
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", marginTop: "8px", gap: "8px" }}>
               <SoftBox sx={{ width: "50%" }}>
                  <TextField
                     label={action.label}
                     helperText={
                        formErrors.includes(`invalid_variable_name_${action.field}`)
                           ? "Variable and Output Reference names cannot contain dots."
                           : formErrors.includes(`duplicate_variable_name_${action.field}`)
                              ? "Variable and Output Reference names must be unique."
                              : formErrors.includes(action.field)
                                 ? `${action.label} is required.`
                                 : ""
                     }
                     error={formErrors?.includes(action.field) || formErrors?.includes(`invalid_variable_name_${action.field}`) || formErrors?.includes(`duplicate_variable_name_${action.field}`)}
                     name={action.field}
                     placeholder=""
                     onChange={handleSetFirstValue}
                     onBlur={() => tryCatchWrapper(handleTextFieldBlur)(action)}
                     value={firstValue}
                     type="text"
                     variant="outlined"
                     required={action.isRequired}
                     InputLabelProps={{ classes: { asterisk: styles.asterisk } }}
                     sx={{ marginTop: "16px", ...styles.w100 }}
                     disabled={isDisabled}
                  />
                  {actionForm[action.field]?.value?.includes("@") && (
                     <SoftTypography color="warning" sx={{ fontSize: "12px", marginTop: "4px", opacity: ".7" }}>
                        ⓘ Field does not support context selection
                     </SoftTypography>
                  )}
               </SoftBox>
               <SoftBox sx={{ width: "50%" }}>
                  <AutocompleteInput
                     action={{ ...action.secondaryInput, parentField: action.field }}
                     fieldValue={secondValue}
                     onChange={handleSetSecondValue}
                     sx={styles.w49}
                     hasSecondaryInput
                     disabled={isDisabled}
                     noValidate
                  />
               </SoftBox>
            </Box>
         ) : action.type === ACTION_TYPES.PASSWORD ? (
            <TextField
               label={action.label}
               width="100%"
               style={{ marginBottom: "12px" }}
               name={action.field}
               placeholder={action.placeholder || ""}
               sx={styles.w100}
               value={firstValue}
               onChange={handleSetFirstValue}
               type="password"
               variant="outlined"
               disabled={isDisabled}
            />
         ) : action.field?.startsWith("reference_var_") ? (
            <SoftBox sx={{ flex: 1, minWidth: 0, width: "100%" }}>
               <TextField
                  label={action.label || undefined}
                  helperText={
                     formErrors.includes(`invalid_variable_name_${action.field}`)
                        ? "Variable and Output Reference names cannot contain dots."
                        : formErrors.includes(`duplicate_variable_name_${action.field}`)
                           ? "Variable and Output Reference names must be unique."
                           : formErrors.includes(action.field)
                              ? "Output Reference is required."
                              : ""
                  }
                  error={formErrors.includes(action.field) || formErrors.includes(`invalid_variable_name_${action.field}`) || formErrors.includes(`duplicate_variable_name_${action.field}`)}
                  name={action.field}
                  placeholder={action.placeholder || ""}
                  onChange={handleSetFirstValue}
                  onBlur={() => tryCatchWrapper(handleTextFieldBlur)(action)}
                  value={firstValue}
                  type="text"
                  variant="outlined"
                  required={action.isRequired}
                  InputLabelProps={{ classes: { asterisk: styles.asterisk } }}
                  sx={styles.w100}
                  disabled={isDisabled}
                  fullWidth
               />
            </SoftBox>
         ) : (
            <SoftBox sx={action.field?.startsWith("reference_var_") ? { flex: 1, minWidth: 0, width: "100%", "& .MuiAutocomplete-root": { width: "100%" } } : undefined}>
               <AutocompleteInput action={action} fieldValue={firstValue} onChange={handleSetFirstValue} disabled={isDisabled} noValidate />
            </SoftBox>
         )}
         {!isDisabled ? (
            <ListItemIcon className="delete-variable-icon-wrapper" sx={action.field?.startsWith("reference_var_") ? { top: "auto", marginTop: 0, alignSelf: "flex-start" } : { marginTop: "8.5px" }}>
               <Tooltip placement="left" title="Delete">
                  <IconButton
                     className="delete-variable-icon"
                     onClick={(event) => {
                        event.stopPropagation();
                        deleteVariable(action.field);
                     }}
                  >
                     <ThemedTrashIcon />
                  </IconButton>
               </Tooltip>
            </ListItemIcon>
         ) : null}
         <Dialog
            open={Boolean(blockedDeletion)}
            onClose={() => setBlockedDeletion(null)}
            aria-labelledby="output-reference-delete-dialog-title"
            aria-describedby="output-reference-delete-dialog-description"
            fullWidth
            maxWidth="sm"
         >
            <DialogTitle id="output-reference-delete-dialog-title">Cannot delete “{blockedDeletion?.name}”</DialogTitle>
            <DialogContent>
               <DialogContentText id="output-reference-delete-dialog-description">This Output Reference is used by:</DialogContentText>
               <Box component="ul" sx={{ mb: 0, pl: 3 }}>
                  {blockedDeletion?.consumers.map((node) => (
                     <li key={node.id}>{getNodeDisplayName(node)}</li>
                  ))}
               </Box>
               <DialogContentText sx={{ mt: 2 }}>Remove or change these node Variable References before deleting it.</DialogContentText>
            </DialogContent>
            <DialogActions>
               <SoftButton variant="outlined" color="primary" onClick={() => setBlockedDeletion(null)}>
                  Close
               </SoftButton>
            </DialogActions>
         </Dialog>
      </>
   );
};
